import {
  normalizeWidgetTheme,
  APIURL,
  cancelSelectEvent,
  changeSymbolForWidget,
  getCSSStyles,
  getEChartsTooltipTheme,
  getSymbolDropdown,
  getTraderSMCardHTML,
  getWidgetDataURI,
  apiGet,
  toggleWidgetTheme,
  getHeaderPinStateClass,
  getHeaderPinButtonHTML,
  setupHeaderPin
} from '../../helpers';
import { InsiderNetworkConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface InsiderNetworkNode {
  insider: string;
  country: string | null;
  total_value: number;
  total_value_millions: number;
  acquisitions: number;
  dispositions: number;
  symbols_count: number;
  role: string;
  transaction_count: number;
  net_flow: number;
  sentiment: string;
  net_icon: string;
  node_size: number;
  display_name: string;
}

interface InsiderNetworkApiData {
  parameters: {
    symbol: string | null;
    country: string | null;
    days_back: number;
    min_transaction_value: number;
    insider_name: string | null;
    show_top_n: number;
    cutoff_date: string;
    query_date: string;
  };
  overall_statistics: {
    total_insiders: number;
    total_transactions: number;
    total_value: number;
    total_acquisitions: number;
    total_dispositions: number;
    total_symbols: number;
  };
  network_nodes: InsiderNetworkNode[];
  layout_data: {
    center_x: number;
    center_y: number;
    radius: number;
    node_count: number;
  };
}

const CENTER_NODE_ID = '__insiders_network_center__';

const INSIDER_COLORS = [
  '#60A5FA',
  '#14B8A6',
  '#3B82F6',
  '#F97316',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
  '#F59E0B',
  '#6366F1',
  '#A78BFA'
];

export class TraderverseInsiderNetwork {
  private container: HTMLElement;
  private config: InsiderNetworkConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private shadowRoot: ShadowRoot;
  private apiData: InsiderNetworkApiData | null = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private filters = {
    symbol: '' as string | undefined,
    daysBack: 90 as number | '',
    minTransactionValue: 500000 as number | '',
    insiderNameInput: '' as string,
    showTopN: 10 as number | ''
  };

  constructor(config: InsiderNetworkConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.applyConfigFilters();
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private getDefaultConfig(): Partial<InsiderNetworkConfig> {
    return {
      headerPinned: true,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      isSymbolSelectable: true,
      symbol: 'AAPL',
      daysBack: 90,
      minTransactionValue: 500000,
      showTopN: 10,
      insiderNameInput: ''
    };
  }

  private applyConfigFilters(): void {
    if (this.config.symbol) this.filters.symbol = this.config.symbol;
    if (typeof this.config.daysBack === 'number') this.filters.daysBack = this.config.daysBack;
    if (typeof this.config.minTransactionValue === 'number') {
      this.filters.minTransactionValue = this.config.minTransactionValue;
    }
    if (typeof this.config.showTopN === 'number') this.filters.showTopN = this.config.showTopN;
    if (typeof this.config.insiderNameInput === 'string') {
      this.filters.insiderNameInput = this.config.insiderNameInput;
    }
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    await this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    await this.fetchData();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private async createWidget(): Promise<void> {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
      themeClass = 'defaultLightMode';
      dataTheme = 'default-lite';
    }

    const widgetHtml = `
      <div style="width: ${typeof this.config.width === 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height === 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Insiders Network</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <div class="item">
              <button class="filters-toggle" type="button" aria-label="Toggle filters">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <i class="pi pi-ellipsis-v"></i>
          </div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            ${this.config.hideSymbol ? '' : `<div class="item dropStyle01">
              <div class="trader-sm-card" id="selected-symbol">
                ${this.config.symbol
                  ? getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol, type: 'stock', name: '' })
                  : '<span>Select Symbol</span>'}
              </div>
              <span id="symbol-dropdown-placeholder"></span>
            </div>`}
            <div class="item">
              <input type="text" class="filter-insider-name" placeholder="Search insider name..." value="${this.escapeHtml(this.filters.insiderNameInput)}" aria-label="Insider name" />
            </div>
            <span></span>
            <div class="filtersBox sideBar hidden">
              <div class="item">
                <label>Days Back</label>
                <input type="number" class="filter-days-back" min="1" step="1" placeholder="e.g. 90" value="${this.filters.daysBack}" />
              </div>
              <div class="item">
                <label>Min Transaction Value</label>
                <input type="number" class="filter-min-transaction-value" min="0" step="1" placeholder="e.g. 500000" value="${this.filters.minTransactionValue}" />
              </div>
              <div class="item">
                <label>Show Top N</label>
                <input type="number" class="filter-show-top-n" min="1" step="1" placeholder="e.g. 10" value="${this.filters.showTopN}" />
              </div>
            </div>
          </div>
          <div class="chartDv insiderNetworkChart" id="insiderNetworkContainer">
            <div id="insiderNetworkChart" class="chart coverDv"></div>
          </div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    await this.attachStyles();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);

    const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
    if (filtersToggle && filtersBox) {
      filtersToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        filtersBox.classList.toggle('hidden');
      });
    }

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) placeholder.replaceWith(dropdown);
    }

    const insiderInput = this.shadowRoot.querySelector('.filter-insider-name') as HTMLInputElement | null;
    const runInsiderSearch = () => {
      this.filters.insiderNameInput = insiderInput?.value.trim() ?? '';
      this.config.insiderNameInput = this.filters.insiderNameInput;
      this.fetchData();
    };
    insiderInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') runInsiderSearch();
    });
    insiderInput?.addEventListener('change', runInsiderSearch);

    const updateNumericFiltersAndFetch = () => {
      this.filters.daysBack = this.readNumberInput('.filter-days-back');
      this.filters.minTransactionValue = this.readNumberInput('.filter-min-transaction-value');
      this.filters.showTopN = this.readNumberInput('.filter-show-top-n');
      if (this.filters.daysBack !== '') this.config.daysBack = this.filters.daysBack;
      if (this.filters.minTransactionValue !== '') this.config.minTransactionValue = this.filters.minTransactionValue;
      if (this.filters.showTopN !== '') this.config.showTopN = this.filters.showTopN;
      this.fetchData();
    };

    ['.filter-days-back', '.filter-min-transaction-value', '.filter-show-top-n'].forEach((selector) => {
      const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
      input?.addEventListener('change', updateNumericFiltersAndFetch);
    });
  }

  private readNumberInput(selector: string): number | '' {
    const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
    if (!input || input.value.trim() === '') return '';
    const value = parseFloat(input.value);
    return Number.isNaN(value) ? '' : value;
  }

  private onSelectChange(stock: any): void {
    this.changeSymbol(stock.symbol, stock);
  }

  private buildQueryParams(): Record<string, string | number> {
    const query: Record<string, string | number> = {};
    if (this.filters.symbol) query.symbol = this.filters.symbol;
    if (this.filters.daysBack !== '') query.daysBack = this.filters.daysBack;
    if (this.filters.minTransactionValue !== '') query.minTransactionValue = this.filters.minTransactionValue;
    if (this.filters.showTopN !== '') query.showTopN = this.filters.showTopN;
    if (this.filters.insiderNameInput.trim()) {
      query.insiderNameInput = this.filters.insiderNameInput.trim();
    }
    return query;
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
      script.async = true;
      script.onload = () => resolve(true);
      this.shadowRoot.appendChild(script);
    });
  }

  private hasChartData(data: InsiderNetworkApiData | null): boolean {
    return (data?.network_nodes?.length ?? 0) > 0;
  }

  private async fetchData(symbol?: string): Promise<void> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const container = this.shadowRoot.querySelector('#insiderNetworkContainer') as HTMLElement;
    if (widget) widget.classList.add('loading');

    if (symbol) {
      this.filters.symbol = symbol;
      this.config.symbol = symbol;
    }

    try {
      const res = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/equity-market/insider-network`,
        query: this.buildQueryParams(),
        headers: { }
      });
      if (!res.ok) throw new Error('Failed to fetch insider network data');

      const json = await res.json();
      this.apiData = (json?.data as InsiderNetworkApiData) ?? null;

      if (!this.hasChartData(this.apiData)) {
        if (this.currentChart) {
          this.currentChart.dispose();
          this.currentChart = null;
        }
        const chartEl = this.shadowRoot.querySelector('#insiderNetworkChart') as HTMLElement;
        if (chartEl) chartEl.innerHTML = '';
        if (container) {
          container.innerHTML = '<div class="noData"><p>No data available</p></div>';
        }
      } else {
        if (container && !container.querySelector('#insiderNetworkChart')) {
          container.innerHTML = '<div id="insiderNetworkChart" class="chart coverDv"></div>';
        }
        this.updateLegend();
        this.drawChart();
      }

      this.emit('dataUpdate', this.apiData);
    } catch (err) {
      console.error('Failed to fetch insider network data:', err);
      this.apiData = null;
      if (container) container.innerHTML = '<div class="noData"><p>Failed to load data</p></div>';
      this.emit('error', err);
    } finally {
      if (widget) widget.classList.remove('loading');
    }
  }

  private updateLegend(): void {
    const legend = this.shadowRoot.querySelector('#insider-network-legend') as HTMLElement | null;
    if (!legend) return;

    const nodes = this.apiData?.network_nodes ?? [];
    const centerDot = `<span data-tooltip="Insiders Network" style="background-color:#8B5CF6;"></span>`;
    const insiderDots = nodes.map((node, index) => {
      const color = INSIDER_COLORS[index % INSIDER_COLORS.length];
      const label = this.escapeHtml(node.display_name || node.insider);
      return `<span data-tooltip="${label}" style="background-color:${color};"></span>`;
    }).join('');

    legend.innerHTML = centerDot + insiderDots;
  }

  private formatValueMillions(value: number): string {
    if (value >= 1) return `$${value.toFixed(2)}M`;
    if (value > 0) return `$${(value * 1000).toFixed(0)}K`;
    return '$0';
  }

  private scaleNodeSize(value: number, minVal: number, maxVal: number, scaleFactor: number): number {
    const minSize = 56;
    const maxSize = 120;
    if (maxVal <= minVal) return Math.round(80 * scaleFactor);
    const ratio = (value - minVal) / (maxVal - minVal);
    return Math.round((minSize + ratio * (maxSize - minSize)) * scaleFactor);
  }

  /** Invisible corner nodes so ECharts keeps a stable aspect box + edge room for circles/labels. */
  private buildBoundNodes(layoutRadius: number, maxNodeSize: number, scaleFactor: number): any[] {
    const halfNode = maxNodeSize / 2;
    const labelRoom = Math.max(90, Math.round(110 * scaleFactor));
    const padY = layoutRadius + halfNode + 12;
    const padX = layoutRadius + halfNode + labelRoom;
    const corners = [
      { id: '__bound_tl', x: -padX, y: -padY },
      { id: '__bound_tr', x: padX, y: -padY },
      { id: '__bound_bl', x: -padX, y: padY },
      { id: '__bound_br', x: padX, y: padY }
    ];
    return corners.map((c) => ({
      ...c,
      name: '',
      symbolSize: 1,
      itemStyle: { opacity: 0 },
      label: { show: false },
      silent: true,
      tooltip: { show: false },
      z: -1
    }));
  }

  private buildGraphData(layoutRadius: number, scaleFactor: number): { nodes: any[]; links: any[] } {
    const insiders = this.apiData?.network_nodes ?? [];
    const values = insiders.map((n) => n.total_value);
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);

    const centerSize = Math.max(28, Math.round(72 * scaleFactor));
    const labelFontSize = Math.max(7, Math.round(11 * scaleFactor));
    const satLabelFontSize = Math.max(9, Math.round(10 * scaleFactor));
    const centerLineHeight = Math.max(10, Math.round(14 * scaleFactor));
    const satLineHeight = Math.max(10, Math.round(13 * scaleFactor));
    const maxNodeSize = Math.max(
      centerSize,
      ...insiders.map((n) => this.scaleNodeSize(n.total_value, minVal, maxVal, scaleFactor))
    );

    const nodes: any[] = [
      ...this.buildBoundNodes(layoutRadius, maxNodeSize, scaleFactor),
      {
        id: CENTER_NODE_ID,
        name: 'Insiders Network',
        x: 0,
        y: 0,
        symbolSize: centerSize,
        itemStyle: { color: '#8B5CF6' },
        label: {
          show: true,
          position: 'inside',
          color: '#FFFFFF',
          fontSize: labelFontSize,
          fontWeight: 600,
          lineHeight: centerLineHeight,
          formatter: 'Insiders\nNetwork'
        }
      }
    ];

    const links: any[] = [];

    insiders.forEach((insider, index) => {
      const angle = insiders.length === 1
        ? -Math.PI / 2
        : (2 * Math.PI * index) / insiders.length - Math.PI / 2;
      const nodeId = `insider_${index}`;
      const color = INSIDER_COLORS[index % INSIDER_COLORS.length];
      const displayName = insider.display_name || insider.insider;
      const valueLabel = this.formatValueMillions(insider.total_value_millions);
      const companiesLabel = `Companies: ${insider.symbols_count}`;
      const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
      const x = Math.cos(angle) * layoutRadius;
      const y = Math.sin(angle) * layoutRadius;
      // Keep labels inside the chart: right-side nodes label to the left
      const labelPosition = x >= 0 ? 'right' : 'left';

      nodes.push({
        id: nodeId,
        name: displayName,
        x,
        y,
        symbolSize: this.scaleNodeSize(insider.total_value, minVal, maxVal, scaleFactor),
        itemStyle: { color },
        insiderData: insider,
        label: {
          show: true,
          position: labelPosition,
          color: textColor,
          fontSize: satLabelFontSize,
          fontWeight: 600,
          lineHeight: satLineHeight,
          formatter: `${displayName}\n${valueLabel}\n${companiesLabel}`
        }
      });

      links.push({
        source: CENTER_NODE_ID,
        target: nodeId,
        lineStyle: {
          color: 'rgba(148, 163, 184, 0.35)',
          width: 1,
          type: 'dashed',
          curveness: 0
        }
      });
    });

    return { nodes, links };
  }

  private updateDisplay(): void {
    if (!this.hasChartData(this.apiData) || !(window as any).echarts) return;
    this.drawChart();
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#insiderNetworkChart') as HTMLElement;
    if (!container || !this.hasChartData(this.apiData)) return;

    if (this.currentChart) {
      this.currentChart.dispose();
    }

    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color').trim() || '#94A3B8';
    const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color').trim() || 'rgba(148, 163, 184, 0.25)';
    const tooltipTheme = getEChartsTooltipTheme(widgetBox);

    const rect = container.getBoundingClientRect();
    const containerSize = Math.min(rect.width, rect.height) || 400;
    const designSize = 500;
    const scaleFactor = Math.max(0.35, Math.min(1, containerSize / designSize));
    // Leave room for node radius + labels; bound nodes handle the rest of the fit
    const layoutRadius = Math.max(40, containerSize * 0.22);
    const { nodes, links } = this.buildGraphData(layoutRadius, scaleFactor);

    const chart = echarts.init(container);
    chart.setOption({
      backgroundColor: 'transparent',
      tooltip: {
        ...tooltipTheme,
        trigger: 'item',
        formatter: (params: any) => {
          if (!params.data?.insiderData) {
            if (params.data?.id === CENTER_NODE_ID) {
              return `<div style="font-weight:600">Insiders Network</div>`;
            }
            return '';
          }
          const d = params.data.insiderData as InsiderNetworkNode;
          return `
            <div style="min-width:160px">
              <div style="font-weight:600;margin-bottom:6px">${d.insider}</div>
              <div>Total Value: <b>${this.formatValueMillions(d.total_value_millions)}</b></div>
              <div>Companies: <b>${d.symbols_count}</b></div>
              <div>Transactions: <b>${d.transaction_count}</b></div>
              <div>Role: ${d.role}</div>
              <div>Sentiment: ${d.sentiment} ${d.net_icon}</div>
            </div>`;
        }
      },
      graphic: [
        {
          type: 'line',
          shape: { x1: '50%', y1: '8%', x2: '50%', y2: '92%' },
          style: { stroke: splitLineColor, lineWidth: 1, lineDash: [4, 4] },
          silent: true,
          z: 0
        },
        {
          type: 'line',
          shape: { x1: '8%', y1: '50%', x2: '92%', y2: '50%' },
          style: { stroke: splitLineColor, lineWidth: 1, lineDash: [4, 4] },
          silent: true,
          z: 0
        }
      ],
      series: [
        {
          type: 'graph',
          layout: 'none',
          coordinateSystem: null,
          roam: false,
          draggable: false,
          focusNodeAdjacency: true,
          symbol: 'circle',
          edgeSymbol: ['none', 'none'],
          data: nodes,
          links,
          lineStyle: {
            color: splitLineColor,
            width: 1,
            type: 'dashed',
            opacity: 0.6
          },
          emphasis: {
            focus: 'adjacency',
            lineStyle: { width: 2, opacity: 0.9 }
          },
          label: {
            color: textColor
          }
        }
      ]
    });

    this.currentChart = chart;
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (symbol: string) => this.fetchData(symbol),
      (type: any, data: any) => this.emit(type, data)
    );
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
    this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    if (this.currentChart) {
      try { this.currentChart.dispose(); } catch { /* ignore */ }
      this.currentChart = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach((h) => h(evt));
    }
  }

  private getTitleIconSvg(): string {
    return `<svg width="27" height="22" viewBox="0 0 27 22" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M22.6125 22H4.3875C3.26681 22.0001 2.18858 21.5634 1.37399 20.7794C0.559395 19.9955 0.0701982 18.9239 0.00675018 17.7843L2.29482e-08 17.5312V3.09375C-9.45295e-05 2.30983 0.291999 1.5551 0.817249 0.982089C1.3425 0.409082 2.06173 0.0605304 2.8296 0.00687518L3.0375 2.33732e-08H19.9125C20.6822 -9.62801e-05 21.4232 0.297406 21.9858 0.832383C22.5484 1.36736 22.8906 2.09991 22.9433 2.882L22.95 3.09375V4.125H23.9625C24.7322 4.1249 25.4732 4.42241 26.0358 4.95738C26.5984 5.49236 26.9406 6.22491 26.9933 7.007L27 7.21875V17.5312C27.0001 18.6727 26.5713 19.7709 25.8016 20.6006C25.032 21.4302 23.9798 21.9285 22.8609 21.9931L22.6125 22ZM4.3875 19.9375H22.6125C23.2056 19.9374 23.777 19.7102 24.2132 19.3008C24.6494 18.8914 24.9184 18.33 24.9669 17.7279L24.975 17.5312V7.21875C24.975 6.96955 24.8864 6.72878 24.7256 6.54097C24.5647 6.35316 24.3426 6.23102 24.1002 6.19712L23.9625 6.1875H22.95V16.8438C22.95 17.093 22.8614 17.3337 22.7006 17.5215C22.5397 17.7093 22.3176 17.8315 22.0752 17.8654L21.9375 17.875C21.6928 17.875 21.4564 17.7847 21.272 17.6209C21.0876 17.4571 20.9677 17.2309 20.9345 16.984L20.925 16.8438V3.09375C20.925 2.84455 20.8364 2.60378 20.6756 2.41597C20.5147 2.22816 20.2926 2.10602 20.0502 2.07212L19.9125 2.0625H3.0375C2.79283 2.06251 2.55644 2.15276 2.37204 2.31656C2.18765 2.48035 2.06773 2.70661 2.03445 2.9535L2.025 3.09375V17.5312C2.02502 18.1351 2.24798 18.7169 2.64962 19.1612C3.05125 19.6054 3.6022 19.8796 4.1931 19.9292L4.3875 19.9375ZM13.8321 14.4375H17.8902C18.1467 14.4376 18.3937 14.5368 18.5811 14.7152C18.7686 14.8936 18.8826 15.1378 18.9 15.3985C18.9175 15.6591 18.8372 15.9169 18.6753 16.1195C18.5134 16.3222 18.282 16.4548 18.0279 16.4904L17.8902 16.5H13.8321C13.5756 16.4999 13.3286 16.4007 13.1412 16.2223C12.9537 16.0439 12.8398 15.7997 12.8223 15.539C12.8048 15.2784 12.8851 15.0206 13.047 14.818C13.2089 14.6153 13.4403 14.4827 13.6944 14.4471L13.8321 14.4375ZM9.77805 9.63188C10.0466 9.63188 10.3041 9.74052 10.494 9.93392C10.6839 10.1273 10.7906 10.3896 10.7906 10.6631V15.4688C10.7906 15.7423 10.6839 16.0046 10.494 16.198C10.3041 16.3914 10.0466 16.5 9.77805 16.5H5.0598C4.79127 16.5 4.53373 16.3914 4.34385 16.198C4.15397 16.0046 4.0473 15.7423 4.0473 15.4688V10.6631C4.0473 10.3896 4.15397 10.1273 4.34385 9.93392C4.53373 9.74052 4.79127 9.63188 5.0598 9.63188H9.77805ZM8.76555 11.6944H6.0723V14.4375H8.76555V11.6944ZM13.8321 9.63188H17.8902C18.1467 9.63196 18.3937 9.73121 18.5811 9.90959C18.7686 10.088 18.8826 10.3322 18.9 10.5928C18.9175 10.8535 18.8372 11.1112 18.6753 11.3139C18.5134 11.5166 18.282 11.6491 18.0279 11.6848L17.8902 11.6944H13.8321C13.5737 11.6971 13.3242 11.599 13.1344 11.4204C12.9447 11.2417 12.8293 10.996 12.8117 10.7334C12.7941 10.4709 12.8757 10.2114 13.0398 10.0082C13.2039 9.8049 13.4381 9.67324 13.6944 9.64012L13.8321 9.63188ZM5.0598 4.81525H17.8902C18.1486 4.81257 18.3981 4.9106 18.5879 5.08925C18.7776 5.2679 18.893 5.51366 18.9106 5.7762C18.9282 6.03875 18.8466 6.29821 18.6825 6.50147C18.5184 6.70472 18.2842 6.83639 18.0279 6.8695L17.8902 6.87775H5.0598C4.80327 6.87767 4.55633 6.77841 4.36888 6.60004C4.18143 6.42166 4.06745 6.17746 4.04997 5.91679C4.03248 5.65612 4.1128 5.3984 4.27468 5.19571C4.43657 4.99303 4.66796 4.86049 4.9221 4.82487L5.0598 4.81525Z" fill="currentColor"/> </svg>`;
}
}

declare global { interface Window { TraderverseInsiderNetwork: typeof TraderverseInsiderNetwork; } }
if (typeof window !== 'undefined') { window.TraderverseInsiderNetwork = TraderverseInsiderNetwork; }
